/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button } from './Button';
import { LoadingButton } from './LoadingButton';
import { IconButton } from './IconButton';

// Avoid prop overriding when the child component does not need such prop
const childOverrideValue = (type, propName, propValue) => {
  if (type === IconButton) {
    return null;
  }
  return { [propName]: propValue };
};

const buttonTypeSpacing = {
  regular: 'sui-gap-4 sm:sui-gap-4',
  icon: 'sui-gap-x-2 sm:sui-gap-x-1 sui-gap-1'
};

const ButtonGroup = ({
  children, orientation, disabled, variant, type = 'regular', responsive
}) => {

  const gapClasses = buttonTypeSpacing[type];
  const isHorizontal = orientation === 'horizontal';
  const isVertical = orientation === 'vertical';
  const classes = classNames('sui-flex', gapClasses,
    {
      'sui-flex-col sui-w-full': isVertical,
      'sui-flex-row': isHorizontal && !responsive,
      'sui-flex-col sui-w-full sm:sui-flex-row sm:sui-w-auto': isHorizontal && responsive
    });

  return (
    <div className={classes}>
      {children.map((child, index) => {
        return React.cloneElement(child, {
          disabled: child.props.disabled || disabled,
          ...childOverrideValue(child.type, 'variant', child.props.variant || variant),
          ...childOverrideValue(child.type, 'fullWidth', isVertical),
          key: index
        });
      })}
    </div>
  );
};

ButtonGroup.displayName = 'ButtonGroup';

ButtonGroup.propTypes = {
  /**
   * The content of the component.
   */
  children: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.oneOf([Button, LoadingButton, IconButton])
  })).isRequired,
  /**
   * The component orientation (layout flow direction).
   */
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  /**
   * The variant to use.
   */
  variant: PropTypes.oneOf(['primary', 'secondary', 'text']),
  /**
   * If true, the component is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Indicates whether the group orientation is responsive or not.
   * When a 'horizontal' oriented group is responsive, it changes to 'vertical' orientation on small screens.
   * @default true
   */
  responsive: PropTypes.bool,
  /**
   * The spacing type to use between the child buttons.
   * For `Button` and `LoadingButton` groups use 'regular'.
   * For `IconButton` groups use 'icon'.
   * @default 'regular'
   */
  type: PropTypes.oneOf(['regular', 'icon'])
};

ButtonGroup.defaultProps = {
  orientation: 'horizontal',
  variant: 'secondary',
  type: 'regular',
  responsive: true,
  disabled: false
};

export { ButtonGroup };
